import { useEffect, useState } from 'react';
import { navLinks } from '@/mocks/cinemaData';

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('overview');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = navLinks
      .map((link) => document.getElementById(link.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? 'border-b border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6 lg:h-[72px]">
        <a href="#top" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-film-line text-lg" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-base font-semibold tracking-tight ${
                scrolled ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              秋霞影院在线观看
            </span>
            <span
              className={`mt-0.5 text-[11px] ${
                scrolled ? 'text-foreground-500' : 'text-background-200'
              }`}
            >
              各端访问方式与内容导览
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="页面分区导航">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                active === link.id
                  ? 'bg-primary-100 font-medium text-primary-700'
                  : scrolled
                    ? 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    : 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#films"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            查看影片导览
          </a>
          <button
            type="button"
            aria-label={open ? '关闭菜单' : '打开菜单'}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className={`flex h-10 w-10 items-center justify-center rounded-md lg:hidden ${
              scrolled ? 'text-foreground-900 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
            }`}
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="grid grid-cols-2 gap-1 px-4 py-3" aria-label="移动端分区导航">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}